import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Copy, Info, Search, ShieldAlert, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CapacityTile } from "@/components/common/CapacityTile";
import {
  TenantFileDto,
  TenantFilesFilters,
  TenantFilesSortBy,
} from "@/types/admin-storage";
import { useTenantFiles } from "@/hooks/useAdminStorage";
import { useDebounce } from "@/hooks/useDebounce";
import { ApiError } from "@/services/BaseService";
import { formatBytes } from "@/utils/formatBytes";
import { copyToClipboard } from "@/utils/clipboard";
import { cn } from "@/lib/utils";

type UploadedFilter = "true" | "false";

const UPLOADED_FILTERS: UploadedFilter[] = ["true", "false"];
const PAGE_SIZE = 25;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const truncateId = (id: string): string => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
};

export const StorageTenantDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const isValidUuid = UUID_PATTERN.test(tenantId);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [uploadedFilter, setUploadedFilter] = useState<UploadedFilter>("true");
  const [sortBy, setSortBy] = useState<TenantFilesSortBy>("filesize");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, uploadedFilter, sortBy, sortOrder]);

  const filters = useMemo<TenantFilesFilters>(
    () => ({
      search: debouncedSearch || undefined,
      uploaded: uploadedFilter === "true",
      sortBy,
      sortOrder,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [debouncedSearch, uploadedFilter, sortBy, sortOrder, page],
  );

  const filesQuery = useTenantFiles(
    isValidUuid ? tenantId : undefined,
    filters,
  );

  const data = filesQuery.data;
  const totalFiles = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalFiles / PAGE_SIZE));
  const isFiltering = debouncedSearch.trim().length > 0;

  const handleSort = (field: string) => {
    const candidate = field as TenantFilesSortBy;
    if (
      candidate !== "filesize" &&
      candidate !== "filename" &&
      candidate !== "createdAt"
    ) {
      return;
    }
    if (sortBy === candidate) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(candidate);
      setSortOrder("desc");
    }
  };

  const handleCopyId = async (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => {
    event.stopPropagation();
    const ok = await copyToClipboard(id);
    if (ok) toast.success(t("storage.tenant.copyIdSuccess"));
  };

  const columns: Column<TenantFileDto>[] = [
    {
      key: "id",
      header: t("storage.tenant.columns.id"),
      render: (_, row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(event) => handleCopyId(event, row.id)}
              className="inline-flex items-center gap-1.5 font-mono text-xs hover:underline"
              aria-label={t("storage.tenant.copyId")}
            >
              <span>{truncateId(row.id)}</span>
              <Copy className="size-3 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{row.id}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: "filename",
      header: t("storage.tenant.columns.filename"),
      sortable: true,
      render: (_, row) => (
        <span className="block max-w-[360px] truncate" title={row.filename}>
          {row.filename}
        </span>
      ),
    },
    {
      key: "filesize",
      header: t("storage.tenant.columns.size"),
      sortable: true,
      render: (_, row) => formatBytes(row.filesize),
    },
    {
      key: "uploaded",
      header: t("storage.tenant.columns.uploaded"),
      render: (_, row) =>
        row.uploaded ? (
          <Badge variant="status_done">
            {t("storage.tenant.uploaded.yes")}
          </Badge>
        ) : (
          <Badge variant="new_secondary">
            {t("storage.tenant.uploaded.no")}
          </Badge>
        ),
    },
    {
      key: "createdAt",
      header: t("storage.tenant.columns.createdAt"),
      sortable: true,
      render: (_, row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.createdAt), "PP")}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {format(new Date(row.createdAt), "PPpp")}
          </TooltipContent>
        </Tooltip>
      ),
    },
  ];

  if (!isValidUuid) {
    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("storage.tenant.invalidId")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => navigate("/storage")}
          className="w-fit"
        >
          {t("storage.tenant.back")}
        </Button>
      </div>
    );
  }

  if (filesQuery.isLoading && !data) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("storage.tenant.loading")}
      </div>
    );
  }

  if (filesQuery.isError) {
    const apiError =
      filesQuery.error instanceof ApiError ? filesQuery.error : null;
    if (apiError?.statusCode === 404) {
      return (
        <div className="flex flex-col gap-3 p-12 max-w-md">
          <div className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="size-5" />
            <span className="text-sm font-medium">
              {t("storage.tenant.notFound")}
            </span>
          </div>
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            onClick={() => navigate("/storage")}
            className="w-fit"
          >
            {t("storage.tenant.back")}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("storage.tenant.loadError")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => filesQuery.refetch()}
          className="w-fit"
        >
          {t("storage.list.retry")}
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const showClaimedTooltip = uploadedFilter === "false";
  const bytesTileLabel = showClaimedTooltip
    ? t("storage.tenant.tiles.claimedBytes")
    : t("storage.tenant.tiles.totalBytes");

  return (
    <div className="flex flex-col gap-10 p-12 max-w-7xl">
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          onClick={() =>
            navigate(`/storage/regions/${encodeURIComponent(data.region)}`)
          }
          className="w-fit"
        >
          <Undo2 className="size-4" />
          {t("storage.tenant.backToRegion", { region: data.region })}
        </Button>
        <header className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">
              {data.tenantName}
            </h1>
            <Badge variant="new_outline" className="font-mono">
              {data.region}
            </Badge>
          </div>
        </header>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <CapacityTile
          label={t("storage.tenant.tiles.files")}
          value={data.total.toLocaleString()}
        />
        <CapacityTile
          label={bytesTileLabel}
          value={
            <div className="flex items-center gap-2">
              <span>{formatBytes(data.totalBytes)}</span>
              {showClaimedTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex"
                      aria-label={t(
                        "storage.tenant.tiles.claimedBytesTooltipAria",
                      )}
                    >
                      <Info className="size-4 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("storage.tenant.tiles.claimedBytesTooltip")}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          }
        />
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] max-w-md">
            <Input
              type="search"
              icon={<Search className="size-4 text-muted-foreground" />}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("storage.tenant.filters.search")}
            />
          </div>
          <div className="flex items-center gap-2" role="radiogroup">
            {UPLOADED_FILTERS.map((value) => (
              <Button
                key={value}
                type="button"
                role="radio"
                aria-checked={uploadedFilter === value}
                variant={
                  uploadedFilter === value ? "new_secondary" : "new_ghost"
                }
                size="sm"
                onClick={() => setUploadedFilter(value)}
                className={cn(
                  "min-w-[110px]",
                  uploadedFilter === value && "font-semibold",
                )}
              >
                {value === "true"
                  ? t("storage.tenant.filters.uploadedYes")
                  : t("storage.tenant.filters.uploadedNo")}
              </Button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-base-border">
          <DataTable
            data={data.data}
            columns={columns}
            getRowKey={(row) => row.id}
            loading={filesQuery.isFetching && data.data.length === 0}
            emptyMessage={
              isFiltering
                ? t("storage.tenant.emptyFiltered")
                : t("storage.tenant.empty")
            }
            withPagination={totalPages > 1}
            pagination={
              totalPages > 1
                ? {
                    currentPage: page,
                    totalPages,
                    onPageChange: setPage,
                  }
                : undefined
            }
            sorting={{
              field: sortBy,
              direction: sortOrder,
              onSort: handleSort,
            }}
          />
        </div>
      </section>
    </div>
  );
};
